import getBatch, {
  type BatchExportSettings,
  type BatchModel,
  type BatchResponse,
  type BatchStatus,
} from '@cartwhl/common/api/getBatch';
import getJobDownload, {
  jobZipDownloadFilename,
} from '@cartwhl/common/api/getJobDownload';
import getJobExportStatus, {
  type JobExportStatusResponse,
} from '@cartwhl/common/api/getJobExportStatus';
import getMotionsByGroup, {
  type MotionsByGroupResponse,
} from '@cartwhl/common/api/getMotionsByGroup';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import {
  triggerBrowserFileDownload,
  waitForAsyncState,
} from '@cartwhl/common/utils';

import * as globalStores from '../../stores/global';
import {
  downloadCameraFbx,
  downloadMotionExportSlots,
  exportSlotsHaveReadyUrl,
  parseMotionExportFilenameSlots,
  parseMotionExportURLSlots,
} from '../shared/batchMotionExportUrls';
import {
  completedJobsMissingPreviewUrl,
  MOTION_PREVIEW_CATCH_UP_POLLS,
} from '../shared/batchMotionPreviewPolling';
import type { FileMetadata } from './types';

const POLLING_INTERVAL = 15000;

type Deps = {
  global: typeof globalStores;
};

type State = {
  processId: string | null;
  processName: string;
  email: string;
  fileMetadata: FileMetadata[];
  status: 'idle' | 'loading' | BatchStatus;
  processingProgress: number;
  processedFilesCount: number;
  estimatedProcessingTime: number;
  downloadUrl: string | null;
  model: BatchModel | null;
  requestedDuration: number | null;
  numTotalJobs: number;
  batchExportSettings: BatchExportSettings | null;
};

const initialState: State = {
  processId: null,
  processName: '',
  email: '',
  fileMetadata: [],
  status: 'idle',
  processingProgress: 0,
  processedFilesCount: 0,
  estimatedProcessingTime: 60,
  downloadUrl: null,
  model: null,
  requestedDuration: null,
  numTotalJobs: 0,
  batchExportSettings: null,
};

class BatchGenerationProcessStore extends BaseStore<State, Deps> {
  private statusPollingInterval: NodeJS.Timeout | null = null;
  /** Extra motion polls after batch COMPLETED while studio MP4 URLs are still empty. */
  private previewCatchUpPollsRemaining = 0;
  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.resumePollingIfNeeded();
    }
  };

  constructor(deps: Deps) {
    super(initialState, deps);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  reset() {
    this.stopStatusPolling();
    this.previewCatchUpPollsRemaining = 0;
    Object.assign(this.state, initialState);
  }

  destroy() {
    this.stopStatusPolling();
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
    super.destroy();
  }

  async loadProcess(processId: string) {
    if (this.state.processId === processId && this.state.status !== 'idle') {
      return;
    }

    this.stopStatusPolling();

    this.state.processId = processId;
    this.state.status = 'loading';
    this.previewCatchUpPollsRemaining = 0;

    let apiKey: string;
    try {
      const apiKeyData = await waitForAsyncState(
        this.deps.global.userStore.state,
        (s) => s.apiKey,
      );
      if (!apiKeyData) {
        globalStores.noticesStore.error(
          'Failed to authenticate. Please try again.',
        );
        this.state.status = 'FAILED';
        return;
      }
      apiKey = apiKeyData.apiKey;
    } catch (err) {
      console.error('Failed to get API key:', err);
      globalStores.noticesStore.error(
        'Failed to authenticate. Please try again.',
      );
      this.state.status = 'FAILED';
      return;
    }

    const [batchResult, motionsResult] = await Promise.all([
      getBatch(processId, apiKey),
      getMotionsByGroup(processId, apiKey),
    ]);

    if (this.state.processId !== processId) {
      return;
    }

    if (batchResult.error || !batchResult.data) {
      console.error('Failed to load batch:', batchResult.error?.message);
      globalStores.noticesStore.error(
        batchResult.error?.message || 'Failed to load batch status',
      );
      this.state.status = 'FAILED';
      return;
    }

    this.updateFromBatchResponse(batchResult.data);

    if (motionsResult.data) {
      this.updateFromMotionsResponse(motionsResult.data);
    }

    const status = this.state.status as State['status'];
    const shouldPollMotions =
      status === 'VALIDATING' ||
      status === 'QUEUED' ||
      status === 'IN_PROGRESS' ||
      ((status === 'COMPLETED' || status === 'FAILED') &&
        completedJobsMissingPreviewUrl(this.state.fileMetadata));

    if (shouldPollMotions) {
      this.startStatusPolling(processId);
    }
  }

  async copyProcessIdToClipboard() {
    if (this.state.processId) {
      await navigator.clipboard.writeText(this.state.processId);
      this.deps.global.noticesStore.info('Copied to clipboard');
    }
  }

  async downloadJob(
    jobID: string,
    displayName: string,
    options?: { bodyIndex?: number; camera?: boolean },
  ) {
    const batchID = this.state.processId;
    if (!batchID) return;

    const file = this.state.fileMetadata.find((f) => f.id === jobID);
    const exportURLs = file?.exportURLs;
    const cameraFbxURL = file?.cameraFbxURL?.trim();

    try {
      if (options?.camera) {
        if (!cameraFbxURL) {
          throw new Error('Camera FBX is not available');
        }
        await downloadCameraFbx({
          cameraFbxURL,
          displayName,
          exportFilenames: file?.exportFilenames,
        });
        return;
      }

      if (exportURLs?.length && exportSlotsHaveReadyUrl(exportURLs)) {
        await downloadMotionExportSlots({
          exportURLs,
          exportFilenames: file?.exportFilenames,
          displayName,
          exportType: this.state.batchExportSettings?.exportType,
          bodyIndex: options?.bodyIndex,
        });
        return;
      }

      const { downloadURL } = await getJobDownload(
        batchID,
        jobID,
        this.apiKey,
        options?.bodyIndex !== undefined
          ? { bodyIndex: options.bodyIndex }
          : undefined,
      );
      const filename = jobZipDownloadFilename(
        displayName,
        jobID,
        options?.bodyIndex,
      );
      await triggerBrowserFileDownload(downloadURL, filename);
    } catch (err) {
      console.error('[BatchGeneration] downloadJob failed:', err);
      this.deps.global.noticesStore.error('Download failed. Please try again.');
    }
  }

  async pollJobZipExportReadiness(jobID: string): Promise<JobExportStatusResponse> {
    const batchID = this.state.processId;
    if (!batchID) {
      return {
        downloadExportsReady: false,
        hasFailedExport: false,
        exportRequestCount: 0,
        expectedExportRequests: 0,
        completedExportRequests: 0,
      };
    }
    try {
      return await getJobExportStatus(batchID, jobID, this.apiKey);
    } catch (err) {
      console.error('[BatchGeneration] pollJobZipExportReadiness failed:', err);
      return {
        downloadExportsReady: false,
        hasFailedExport: false,
        exportRequestCount: 0,
        expectedExportRequests: 0,
        completedExportRequests: 0,
      };
    }
  }

  stopPolling() {
    this.stopStatusPolling();
  }

  private get apiKey(): string {
    return this.deps.global.userStore.state.apiKey.data?.apiKey ?? '';
  }

  private updateFromBatchResponse(data: BatchResponse) {
    this.state.status = data.status;
    this.state.processName = data.batchName;
    this.state.email = data.emailSubscribers[0] ?? '';
    this.state.estimatedProcessingTime = data.estimatedSecondsWaitTime ?? 0;
    this.state.downloadUrl = data.downloadURL;
    this.state.model = data.model ?? null;
    this.state.requestedDuration = data.requestedDuration ?? null;
    this.state.numTotalJobs = data.numTotalJobs ?? 0;
    this.state.batchExportSettings = data.exportSettings ?? null;
  }

  private updateFromMotionsResponse(data: MotionsByGroupResponse) {
    const items = data.items ?? [];

    this.state.fileMetadata = items.map((item) => {
      const exportURLs = parseMotionExportURLSlots(
        item.exportURLs ?? item.export_urls,
      );
      const exportFilenames = parseMotionExportFilenameSlots(
        item.exportFilenames ?? item.export_filenames,
      );
      const rawUrls = item.bvhURLs ?? item.bvh_urls;
      const bvhURLs = rawUrls?.map((u) => u.trim()).filter(Boolean);
      const mhrBvhs = item.mhrBvhURLs;
      const mhrIds = item.mhrIdentityJsonURLs;
      const mhrAligned =
        !!mhrBvhs?.length &&
        !!mhrIds?.length &&
        mhrBvhs.length === mhrIds.length;
      return {
        id: item.jobID,
        name: item.title,
        status: item.status,
        ...(exportURLs?.length ? { exportURLs, exportFilenames } : {}),
        bvhURL: item.bvhURL?.trim() ? item.bvhURL : undefined,
        bvhURLs: bvhURLs?.length ? bvhURLs : undefined,
        mhrBvhURLs: mhrAligned ? mhrBvhs : undefined,
        mhrIdentityJsonURLs: mhrAligned ? mhrIds : undefined,
        cameraFbxURL: item.cameraFbxURL?.trim()
          ? item.cameraFbxURL
          : undefined,
        studioPreviewURL: item.studioPreviewURL?.trim()
          ? item.studioPreviewURL
          : undefined,
        cwmsURL: item.cwmsURL?.trim() ? item.cwmsURL : undefined,
        sourceVideoMediaID: item.sourceVideoMediaID?.trim()
          ? item.sourceVideoMediaID
          : undefined,
      };
    });

    const terminalCount = items.filter(
      (m) =>
        m.status === 'COMPLETED' ||
        m.status === 'FAILED' ||
        m.status === 'CANCELLED',
    ).length;

    this.state.processedFilesCount = terminalCount;

    if (items.length > 0) {
      this.state.processingProgress = (terminalCount / items.length) * 100;
    }
  }

  private afterPollUpdates() {
    const batchDone =
      this.state.status === 'COMPLETED' || this.state.status === 'FAILED';

    if (!batchDone) {
      return;
    }

    if (completedJobsMissingPreviewUrl(this.state.fileMetadata)) {
      if (this.previewCatchUpPollsRemaining <= 0) {
        this.previewCatchUpPollsRemaining = MOTION_PREVIEW_CATCH_UP_POLLS;
      }
      this.previewCatchUpPollsRemaining--;
      if (this.previewCatchUpPollsRemaining <= 0) {
        this.stopStatusPolling();
      }
      return;
    }

    this.previewCatchUpPollsRemaining = 0;
    this.stopStatusPolling();
  }

  private startStatusPolling(processId: string) {
    this.stopStatusPolling();

    this.statusPollingInterval = setInterval(async () => {
      const [batchResult, motionsResult] = await Promise.all([
        getBatch(processId, this.apiKey),
        getMotionsByGroup(processId, this.apiKey),
      ]);

      if (processId !== this.state.processId) {
        this.stopStatusPolling();
        return;
      }

      if (batchResult.data) {
        this.updateFromBatchResponse(batchResult.data);
      }

      if (motionsResult.data) {
        this.updateFromMotionsResponse(motionsResult.data);
      }

      this.afterPollUpdates();
    }, POLLING_INTERVAL);
  }

  private async resumePollingIfNeeded() {
    const processId = this.state.processId;
    if (!processId) return;

    const status = this.state.status;
    const batchInFlight =
      status === 'VALIDATING' ||
      status === 'QUEUED' ||
      status === 'IN_PROGRESS';
    const batchDoneNeedingPreviews =
      (status === 'COMPLETED' || status === 'FAILED') &&
      completedJobsMissingPreviewUrl(this.state.fileMetadata);

    if (!batchInFlight && !batchDoneNeedingPreviews) {
      return;
    }

    const [batchResult, motionsResult] = await Promise.all([
      getBatch(processId, this.apiKey),
      getMotionsByGroup(processId, this.apiKey),
    ]);

    if (batchResult.data) {
      this.updateFromBatchResponse(batchResult.data);
    }
    if (motionsResult.data) {
      this.updateFromMotionsResponse(motionsResult.data);
    }

    const nextStatus = this.state.status;
    const stillInFlight =
      nextStatus === 'VALIDATING' ||
      nextStatus === 'QUEUED' ||
      nextStatus === 'IN_PROGRESS';
    const stillNeedPreviews =
      (nextStatus === 'COMPLETED' || nextStatus === 'FAILED') &&
      completedJobsMissingPreviewUrl(this.state.fileMetadata);

    if (stillInFlight || stillNeedPreviews) {
      this.startStatusPolling(processId);
    } else {
      this.afterPollUpdates();
    }
  }

  private stopStatusPolling() {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: BatchGenerationProcessStore,
  displayName: 'BatchGenerationProcessStore',
  staticDeps: { global: globalStores },
});

export type { BatchGenerationProcessStore };
